import { Fragment, useState } from "react";
import {
  Box,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import dayjs from "dayjs";
import { FormulaDecision } from "../../types/formula-scorer.type";
import { fs } from "./tokens";
import { DerivedRow, fmtBaht, fmtPct } from "./derive";
import FormulaChip from "./FormulaChip";
import RowDetail from "./RowDetail";

const headSx = {
  fontSize: 12,
  fontWeight: 700,
  color: fs.dim,
  borderColor: fs.line,
  bgcolor: fs.surface2,
  whiteSpace: "nowrap",
} as const;

const cellSx = { borderColor: fs.line, verticalAlign: "top", py: 1.25 } as const;

function VerdictBadge({ row }: { row: DerivedRow }) {
  const { recommendation, decision } = row.result;
  let color: string = fs.muted;
  let bg: string = fs.mutedBg;
  let text = "ข้อมูลไม่พอ";
  if (recommendation === "switch") {
    color = fs.pos;
    bg = fs.posBg;
    text = "ควรสลับ →";
    if (decision === "confirmed") text = "ยืนยันแล้ว ✓";
    else if (decision === "rejected") text = "ปฏิเสธ";
    else text = "รอยืนยัน";
  } else if (recommendation === "keep") {
    color = fs.keep;
    bg = fs.keepBg;
    text = "คงเดิม";
  }
  return (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        px: 1,
        py: 0.25,
        borderRadius: 1.5,
        bgcolor: bg,
        color,
        fontSize: 12,
        fontWeight: 700,
      }}
    >
      {text}
    </Box>
  );
}

function Row({
  row,
  onDecided,
  onReload,
}: {
  row: DerivedRow;
  onDecided: (customerCode: string, decision: FormulaDecision) => void;
  onReload: () => void;
}) {
  const [open, setOpen] = useState(false);
  const { result } = row;
  return (
    <Fragment>
      <TableRow hover sx={{ "& > td": cellSx }}>
        {/* customer */}
        <TableCell>
          <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: fs.text }}>
            {result.customer_name || result.customer_code}
          </Typography>
          <Typography sx={{ fontSize: 11, color: fs.faint }}>
            {result.customer_code}
          </Typography>
        </TableCell>
        {/* current formula */}
        <TableCell>
          <FormulaChip code={result.current_formula_code} />
          <Typography sx={{ fontSize: 10.5, color: fs.faint, mt: 0.25 }}>
            {result.current_since
              ? `ตั้งแต่ ${dayjs(result.current_since).format("D MMM YY")}`
              : "ค่าเริ่มต้น"}
          </Typography>
        </TableCell>
        {/* data measured */}
        <TableCell>
          <Typography sx={{ fontSize: 12.5, color: fs.text }}>{row.rounds} รอบ</Typography>
          <Typography sx={{ fontSize: 10.5, color: fs.faint }}>
            ครอบคลุม {row.coverageDays} วัน
          </Typography>
        </TableCell>
        {/* challenger */}
        <TableCell>
          {result.recommendation === "switch" && result.winner_formula_code ? (
            <FormulaChip code={result.winner_formula_code} />
          ) : (
            <Typography sx={{ fontSize: 13, color: fs.faint }}>—</Typography>
          )}
        </TableCell>
        {/* gain */}
        <TableCell>
          {result.recommendation === "switch" ? (
            <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75, flexWrap: "wrap" }}>
              <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: fs.pos }}>
                +฿{fmtBaht(row.gainPerRound)}
                <Typography component="span" sx={{ fontSize: 10, color: fs.faint }}>
                  /รอบ
                </Typography>
              </Typography>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: fs.pos }}>
                +{fmtPct(row.gainPct)}%
              </Typography>
              {row.isLowMoney && (
                <Box
                  component="span"
                  sx={{
                    fontSize: 10,
                    color: fs.warn,
                    bgcolor: fs.warnBg,
                    px: 0.5,
                    borderRadius: 1,
                  }}
                >
                  เงินน้อย
                </Box>
              )}
            </Box>
          ) : (
            <Typography sx={{ fontSize: 13, color: fs.faint }}>—</Typography>
          )}
        </TableCell>
        {/* verdict */}
        <TableCell>
          <VerdictBadge row={row} />
        </TableCell>
        {/* expand */}
        <TableCell padding="checkbox">
          <IconButton size="small" onClick={() => setOpen((v) => !v)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={7} sx={{ p: 0, borderColor: fs.line }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <RowDetail row={row} onDecided={onDecided} onReload={onReload} />
          </Collapse>
        </TableCell>
      </TableRow>
    </Fragment>
  );
}

export default function CustomerTable({
  rows,
  onDecided,
  onReload,
}: {
  rows: DerivedRow[];
  onDecided: (customerCode: string, decision: FormulaDecision) => void;
  onReload: () => void;
}) {
  return (
    <Box
      sx={{
        border: `1px solid ${fs.line}`,
        borderRadius: 3,
        overflow: "hidden",
        bgcolor: fs.surface,
      }}
    >
      <Box sx={{ overflowX: "auto" }}>
        <Table sx={{ minWidth: 820 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={headSx}>ลูกค้า / สายส่ง</TableCell>
              <TableCell sx={headSx}>สูตรที่ใช้ตอนนี้</TableCell>
              <TableCell sx={headSx}>ข้อมูลที่วัดได้</TableCell>
              <TableCell sx={headSx}>ผู้ท้าชิงที่ดีที่สุด</TableCell>
              <TableCell sx={headSx}>กำไรเพิ่มถ้าสลับ</TableCell>
              <TableCell sx={headSx}>ผลตัดสิน</TableCell>
              <TableCell sx={headSx} padding="checkbox">
                ดู
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <Row
                key={row.result.customer_code}
                row={row}
                onDecided={onDecided}
                onReload={onReload}
              />
            ))}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
}
